/**
 * "Remember me for 30 days" — Amplify v6 token-storage selection.
 *
 * The 30-day window is the Cognito **refresh-token lifetime** configured
 * on the user pool — the FE cannot and does not change that. The checkbox
 * controls only WHERE Amplify persists the tokens:
 *
 *   - checked   → `localStorage` (survives a browser restart; the refresh
 *                 token keeps the session alive until Cognito expires it).
 *   - unchecked → `sessionStorage` (cleared when the tab/browser closes,
 *                 so the next launch requires a fresh sign-in).
 *
 * CRITICAL: we never hand-write tokens. We hand Amplify's own
 * `cognitoUserPoolsTokenProvider` the storage adapter to use via the
 * documented `setKeyValueStorage(...)` API; Amplify then reads/writes the
 * Cognito tokens through that adapter. This honours the locked repo rule
 * "No `localStorage` token writes (Amplify defaults only)" — the writes
 * are Amplify's, through Amplify's sanctioned storage slot.
 *
 * The choice itself is a non-secret boolean; persisting it (so the next
 * cold boot configures the right adapter BEFORE Amplify rehydrates the
 * session) is fine in localStorage.
 */
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { defaultStorage, sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';

/** localStorage key recording the user's remember-me preference. */
export const REMEMBER_ME_KEY = 'optimalhr.rememberMe';

/**
 * Resolve the persisted remember-me choice. Defaults to `false`
 * (session-only) when unset or storage is unavailable.
 *
 * @returns `true` when the user opted into persistent (localStorage) tokens.
 */
export function readRememberMe(): boolean {
  try {
    return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Persist the remember-me choice so the next cold boot can pick the right
 * Amplify storage adapter before the session rehydrates.
 *
 * @param remember - the checkbox state at sign-in time.
 */
export function persistRememberMe(remember: boolean): void {
  try {
    if (remember) localStorage.setItem(REMEMBER_ME_KEY, 'true');
    else localStorage.removeItem(REMEMBER_ME_KEY);
  } catch {
    /* private mode / storage disabled — fall back to in-memory default */
  }
}

/**
 * Point Amplify's Cognito token provider at the storage adapter matching
 * the remember-me choice. Must run BEFORE the session is established (or
 * rehydrated) so tokens land in the intended store.
 *
 * @param remember - `true` → persistent localStorage; `false` → sessionStorage.
 */
export function applyTokenStorage(remember: boolean): void {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    remember ? defaultStorage : amplifySessionStorage,
  );
}

/**
 * Persist the choice AND apply the matching storage adapter in one call.
 * The sign-in page invokes this the instant the user submits so the
 * tokens Amplify mints land in the right place.
 *
 * @param remember - the checkbox state at sign-in time.
 */
export function setRememberMe(remember: boolean): void {
  persistRememberMe(remember);
  applyTokenStorage(remember);
}

/**
 * Boot-time storage configuration — reads the persisted choice and applies
 * the adapter so a returning user's session rehydrates from the correct
 * store. Called once from `main.ts` before `refreshFromSession()`.
 */
export function configureTokenStorageFromPreference(): void {
  applyTokenStorage(readRememberMe());
}
